import { useEffect, useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, Empty, ErrorBox, Kbd, Loading, formatDate } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import {
  APPROVAL_LEVEL_LABELS,
  RELATIONSHIPS,
  type NetworkAccountDetail,
  type NetworkAccountList,
  type NetworkAccountRow,
  type ProgramMemberships,
  type Relationship,
} from './types.ts';

const BUSINESS_MODELS = ['voucher', 'content', 'cashback', 'other'] as const;
const ACCESS_MODES = { owned: 'Our login', user_access: 'Partner gave us user access', partner_managed: 'Partner operates it' } as const;
const STATUSES = ['active', 'paused', 'closed'] as const;
const REASONS = { none: 'no ad space', empty: 'ad space without id', inactive: 'ad space or account inactive' } as const;
const label = (value: string) => value.replace(/_/g, ' ');

/**
 * Network accounts (D50): the operator's accounts (one per network × legal entity × business model × currency) and
 * partners' own accounts, each site's ad space on them, and the sites that still miss one.
 */
export function NetworkAccounts() {
  const { query, setQuery } = useRouter();
  const changes = useChanges();
  const networkRef = query.get('network') ?? '';
  const list = useOp<NetworkAccountList>('admin.networkAccounts.list', networkRef ? { networkRef } : {}, changes);
  const rows = list.data?.rows ?? [];
  const [cursor, setCursor] = useState(0);
  const open = query.get('account');
  useEffect(() => setCursor(0), [networkRef]);

  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, rows.length)),
      k: () => setCursor((c) => step(c, -1, rows.length)),
      o: () => rows[cursor] && setQuery({ account: rows[cursor]!.ref }),
      enter: () => rows[cursor] && setQuery({ account: rows[cursor]!.ref }),
      n: () => list.data?.canCreate && setQuery({ account: 'new' }),
    },
    !open,
  );

  const current = open && open !== 'new' ? rows.find((r) => r.ref === open) : null;
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Network accounts</h1>
          <p className="muted">Our accounts and partners’ own accounts per network, each site’s ad space on them, and which sites still miss one.</p>
        </div>
        {list.data?.canCreate ? (
          <button type="button" className="btn btn-primary" onClick={() => setQuery({ account: 'new' })}>
            New account <Kbd>n</Kbd>
          </button>
        ) : null}
      </header>
      <div className="toolbar">
        <label className="inline">
          Network{' '}
          <select value={networkRef} onChange={(e) => setQuery({ network: e.target.value || null }, { replace: true })}>
            <option value="">All networks</option>
            {list.data?.networks.map((n) => (
              <option key={n.ref} value={n.ref}>
                {n.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {list.data?.missing.length ? (
        <div className="why-not" role="status" data-missing-ad-spaces>
          <strong>Sites without a usable ad space</strong>
          <ul>
            {list.data.missing.map((m) => (
              <li key={`${m.site.ref}-${m.network.ref}`}>
                {m.site.name} on {m.network.name}: {REASONS[m.reason]} · {m.merchants} merchant(s) cannot get links.{' '}
                <Link to={`/deeplinks?network=${m.network.ref}&tab=spaces`}>Set the ad space</Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {list.data && rows.length === 0 ? <Empty>No network accounts yet.</Empty> : null}
      {rows.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Account</th>
              <th scope="col">Network</th>
              <th scope="col">Owner</th>
              <th scope="col">Model · currency</th>
              <th scope="col" className="num">Ad spaces</th>
              <th scope="col" className="num">Approved programs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.ref} className={index === cursor ? 'cursor' : undefined} onClick={() => setQuery({ account: row.ref })} onMouseEnter={() => setCursor(index)}>
                <td>
                  <span className="strong">{row.name}</span> {row.status !== 'active' ? <span className="badge badge-muted">{row.status}</span> : null}
                  <div className="muted small mono">{row.externalAccountId ?? 'no account id'}</div>
                </td>
                <td>
                  {row.network.name}
                  <div className="muted small">{APPROVAL_LEVEL_LABELS[row.network.approvalLevel]}</div>
                </td>
                <td>{row.owner.isOperator ? <span className="badge badge-ok">{row.owner.name}</span> : <span className="badge badge-warn">{row.owner.name}</span>}</td>
                <td>
                  {row.businessModel} · {row.payoutCurrency}
                </td>
                <td className="num">{row.adSpaces}</td>
                <td className="num">{row.approvedPrograms}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {list.data && open === 'new' ? (
        <AccountForm data={list.data} account={null} defaultNetwork={networkRef} onClose={() => setQuery({ account: null })} onSaved={(ref) => setQuery({ account: ref }, { replace: true })} />
      ) : null}
      {list.data && current ? <AccountDrawer key={current.ref} data={list.data} account={current} onClose={() => setQuery({ account: null })} /> : null}
    </section>
  );
}

function AccountDrawer({ data, account, onClose }: { data: NetworkAccountList; account: NetworkAccountRow; onClose: () => void }) {
  const changes = useChanges();
  const detail = useOp<NetworkAccountDetail>('admin.networkAccounts.get', { ref: account.ref }, changes);
  const [tab, setTab] = useState<'spaces' | 'settings' | 'programs'>('spaces');
  return (
    <Drawer title={account.name} onClose={onClose} wide>
      <div className="segmented tabs" role="tablist" aria-label="Account sections">
        {(['spaces', 'settings', 'programs'] as const).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} aria-pressed={tab === t} onClick={() => setTab(t)}>
            {t === 'spaces' ? 'Ad spaces' : t === 'settings' ? 'Settings' : 'Programs'}
          </button>
        ))}
      </div>
      {detail.error ? <ErrorBox error={detail.error} onRetry={detail.reload} /> : null}
      {!detail.data && detail.loading ? <Loading /> : null}
      {tab === 'settings' ? <AccountForm data={data} account={account} embedded onClose={onClose} onSaved={() => undefined} /> : null}
      {tab === 'spaces' && detail.data ? <AdSpaces detail={detail.data} /> : null}
      {tab === 'programs' && detail.data ? (
        detail.data.memberships.length ? (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Program</th>
                <th scope="col">Ad space</th>
                <th scope="col">Relationship</th>
                <th scope="col">Approved</th>
              </tr>
            </thead>
            <tbody>
              {detail.data.memberships.map((m) => (
                <tr key={m.id}>
                  <td>
                    <Link to={`/programs?program=${m.program.ref}`}>{m.program.name}</Link>
                  </td>
                  <td>{m.site?.name ?? <span className="muted">whole account</span>}</td>
                  <td>
                    <span className={`badge ${m.relationshipStatus === 'approved' ? 'badge-ok' : 'badge-warn'}`}>{label(m.relationshipStatus)}</span>
                  </td>
                  <td>{m.approvedAt ? formatDate(m.approvedAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No program relationships recorded on this account yet.</Empty>
        )
      ) : null}
    </Drawer>
  );
}

function AdSpaces({ detail }: { detail: NetworkAccountDetail }) {
  return (
    <>
      {detail.adSpaces.length ? (
        <table className="table" data-ad-spaces>
          <thead>
            <tr>
              <th scope="col">Site</th>
              <th scope="col">Ad space id(s)</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {detail.adSpaces.map((space) => (
              <AdSpaceRow key={space.site.ref} account={detail.account} space={space} />
            ))}
          </tbody>
        </table>
      ) : (
        <Empty>No site carries its links on this account yet.</Empty>
      )}
      {detail.missingSites.length ? (
        <div className="why-not">
          <strong>Sites without a usable ad space on {detail.account.network.name}</strong>
          <ul>
            {detail.missingSites.map((site) => (
              <li key={site.ref}>
                {site.name}: {REASONS[site.reason]} ({site.merchants} merchant(s) waiting) <AddSpace account={detail.account} siteRef={site.ref} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

function AdSpaceRow({ account, space }: { account: NetworkAccountRow; space: NetworkAccountDetail['adSpaces'][number] }) {
  const toast = useToast();
  const [draft, setDraft] = useState(space.ids);
  useEffect(() => setDraft(space.ids), [space.ids]);
  const save = async () => {
    if (draft.trim() === space.ids) return;
    await runWrite<{ warnings: string[] }>(
      toast,
      'admin.adSpaces.save',
      { networkRef: account.network.ref, siteRef: space.site.ref, adSpaces: draft.trim(), accountRef: account.ref, useAsSiteValue: false },
      (r) => (r.warnings.length ? `${space.site.name}: saved — ${r.warnings[0]}` : `${space.site.name}: saved`),
    );
  };
  return (
    <tr>
      <td>
        {space.site.name}
        <div className="muted small">{space.site.organization}</div>
      </td>
      <td>
        <input
          className="mono"
          aria-label={`Ad space id on ${space.site.name}`}
          value={draft}
          disabled={!space.canManage}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setDraft(space.ids);
          }}
        />
        {space.status !== 'active' ? <span className="badge badge-muted">{space.status}</span> : null}
      </td>
      <td>
        {space.errors.map((e) => (
          <div key={e} className="text-warn small">
            {e}
          </div>
        ))}
        {space.warnings.map((w) => (
          <div key={w} className="text-warn small">
            {w}
          </div>
        ))}
        {space.errors.length + space.warnings.length === 0 ? <span className="text-ok small">ok</span> : null}
      </td>
    </tr>
  );
}

function AddSpace({ account, siteRef }: { account: NetworkAccountRow; siteRef: string }) {
  const toast = useToast();
  const [value, setValue] = useState('');
  return (
    <span className="aff-inline">
      <input className="mono" placeholder="ad space id" aria-label="New ad space id" value={value} onChange={(e) => setValue(e.target.value)} />
      <button
        type="button"
        className="btn btn-small"
        disabled={value.trim() === ''}
        onClick={() => runWrite(toast, 'admin.adSpaces.save', { networkRef: account.network.ref, siteRef, adSpaces: value.trim(), accountRef: account.ref, useAsSiteValue: false }, 'Ad space added')}
      >
        Add
      </button>
    </span>
  );
}

function AccountForm({
  data,
  account,
  defaultNetwork,
  embedded = false,
  onClose,
  onSaved,
}: {
  data: NetworkAccountList;
  account: NetworkAccountRow | null;
  defaultNetwork?: string;
  embedded?: boolean;
  onClose: () => void;
  onSaved: (ref: string) => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState(() => ({
    networkRef: account?.network.ref ?? (defaultNetwork || data.networks[0]?.ref) ?? '',
    ownerRef: account?.owner.ref ?? data.owners[0]?.ref ?? '',
    name: account?.name ?? '',
    legalEntity: account?.legalEntity ?? '',
    businessModel: account?.businessModel ?? 'voucher',
    payoutCurrency: account?.payoutCurrency ?? 'GBP',
    region: account?.region ?? '',
    externalAccountId: account?.externalAccountId ?? '',
    accessMode: account?.accessMode ?? 'owned',
    credentialName: Object.values(account?.credentialRefs ?? {})[0] ?? '',
    status: account?.status ?? 'active',
    notes: account?.notes ?? '',
  }));
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const readOnly = account ? !account.canManage : !data.canCreate;
  const owners = account && !data.owners.some((o) => o.ref === account.owner.ref) ? [...data.owners, account.owner] : data.owners;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const result = await runWrite<{ ref: string; created: boolean }>(
      toast,
      'admin.networkAccounts.save',
      {
        ...(account ? { ref: account.ref } : {}),
        networkRef: draft.networkRef,
        ownerRef: draft.ownerRef,
        name: draft.name,
        legalEntity: draft.legalEntity || null,
        businessModel: draft.businessModel,
        payoutCurrency: draft.payoutCurrency,
        region: draft.region || null,
        externalAccountId: draft.externalAccountId || null,
        accessMode: draft.accessMode,
        credentialRefs: draft.credentialName ? { apiToken: draft.credentialName } : {},
        status: draft.status,
        notes: draft.notes || null,
      },
      (r) => (r.created ? `${draft.name} created` : `${draft.name} saved`),
    );
    setSaving(false);
    if (result) onSaved(result.ref);
  };

  const form = (
    <form onSubmit={submit}>
      <fieldset disabled={readOnly || saving} className="form-grid" style={{ border: 0, padding: 0, margin: 0 }}>
        <label>
          Network
          <select value={draft.networkRef} disabled={Boolean(account)} onChange={(e) => set('networkRef', e.target.value)}>
            {data.networks.map((n) => (
              <option key={n.ref} value={n.ref}>
                {n.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Owner
          <select value={draft.ownerRef} onChange={(e) => set('ownerRef', e.target.value)}>
            {owners.map((o) => (
              <option key={o.ref} value={o.ref}>
                {o.name}
                {o.isOperator ? ' (platform)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="span-2">
          Name
          <input required minLength={2} value={draft.name} onChange={(e) => set('name', e.target.value)} />
        </label>
        <label>
          Account id on the network
          <input className="mono" placeholder="publisher / company id" value={draft.externalAccountId} onChange={(e) => set('externalAccountId', e.target.value)} />
        </label>
        <label>
          Legal entity
          <input value={draft.legalEntity} onChange={(e) => set('legalEntity', e.target.value)} />
        </label>
        <label>
          Business model
          <select value={draft.businessModel} onChange={(e) => set('businessModel', e.target.value as typeof draft.businessModel)}>
            {BUSINESS_MODELS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </label>
        <label>
          Payout currency
          <input required maxLength={3} minLength={3} value={draft.payoutCurrency} onChange={(e) => set('payoutCurrency', e.target.value.toUpperCase())} />
        </label>
        <label>
          Access
          <select value={draft.accessMode} onChange={(e) => set('accessMode', e.target.value as typeof draft.accessMode)}>
            {(Object.keys(ACCESS_MODES) as (keyof typeof ACCESS_MODES)[]).map((m) => (
              <option key={m} value={m}>
                {ACCESS_MODES[m]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select value={draft.status} onChange={(e) => set('status', e.target.value as typeof draft.status)}>
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
        <label>
          Region
          <input placeholder="e.g. UK, EU" value={draft.region} onChange={(e) => set('region', e.target.value)} />
        </label>
        <label>
          API token secret <small className="muted">name in the secret store, never the token</small>
          <input className="mono" placeholder="AWIN_API_TOKEN" pattern="[A-Za-z][A-Za-z0-9_./\-]*" value={draft.credentialName} onChange={(e) => set('credentialName', e.target.value)} />
        </label>
        <label className="span-2">
          Notes
          <textarea rows={2} value={draft.notes} onChange={(e) => set('notes', e.target.value)} />
        </label>
      </fieldset>
      {readOnly ? <p className="muted">Only the account’s organization (its owners and admins) or the platform operator can change it.</p> : null}
      <div className="form-actions">
        {!readOnly ? (
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {account ? 'Save' : 'Create account'}
          </button>
        ) : null}
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </form>
  );
  return embedded ? form : (
    <Drawer title="New network account" onClose={onClose}>
      {form}
    </Drawer>
  );
}

/** Our relationships with a program per account (and per ad space where the network approves sites), shown in the program drawer. */
export function ProgramMembershipsPanel({ programRef }: { programRef: string }) {
  const toast = useToast();
  const changes = useChanges();
  const data = useOp<ProgramMemberships>('admin.programs.memberships', { programRef }, changes);
  const [accountRef, setAccountRef] = useState('');
  const [siteRef, setSiteRef] = useState('');
  const [status, setStatus] = useState<Relationship>('approved');
  if (data.error) return <ErrorBox error={data.error} onRetry={data.reload} />;
  if (!data.data) return null;
  const { approvalLevel, memberships, accounts } = data.data;
  const manageable = accounts.filter((a) => a.canManage);
  const chosen = manageable.find((a) => a.ref === accountRef) ?? manageable[0];
  const save = (input: { accountRef: string; siteRef: string | null; relationshipStatus: Relationship }) =>
    runWrite(toast, 'admin.programs.saveMembership', { programRef, ...input }, 'Relationship saved');

  return (
    <section data-memberships>
      <h3 className="section-title">Relationships per account</h3>
      <p className="muted small">{APPROVAL_LEVEL_LABELS[approvalLevel]}.</p>
      {memberships.length ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Account</th>
              <th scope="col">Ad space</th>
              <th scope="col">Relationship</th>
              <th scope="col">Current deal</th>
            </tr>
          </thead>
          <tbody>
            {memberships.map((m) => (
              <tr key={m.id}>
                <td>
                  {m.account.name}
                  <div className="muted small">{m.account.owner}</div>
                </td>
                <td>{m.site?.name ?? <span className="muted">all sites on the account</span>}</td>
                <td>
                  <select
                    aria-label={`Relationship through ${m.account.name}`}
                    value={m.relationshipStatus}
                    disabled={!m.canManage}
                    onChange={(e) => save({ accountRef: m.account.ref, siteRef: m.site?.ref ?? null, relationshipStatus: e.target.value as Relationship })}
                  >
                    {RELATIONSHIPS.map((r) => (
                      <option key={r} value={r}>
                        {label(r)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="small">
                  {m.currentTerms ? (
                    <>
                      {describeTerms(m.currentTerms.terms)} <span className="muted">since {formatDate(m.currentTerms.validFrom)} · {m.currentTerms.source}</span>
                    </>
                  ) : (
                    <span className="muted">none recorded</span>
                  )}
                  {m.termsHistory > 1 ? <span className="muted"> · {m.termsHistory} deals</span> : null}
                  {m.canManage ? <AddTerm membershipId={m.id} /> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">No relationship recorded through any account yet.</p>
      )}
      {chosen ? (
        <div className="aff-inline" data-add-membership>
          <select aria-label="Account" value={chosen.ref} onChange={(e) => setAccountRef(e.target.value)}>
            {manageable.map((a) => (
              <option key={a.ref} value={a.ref}>
                {a.name}
              </option>
            ))}
          </select>
          {approvalLevel === 'ad_space' ? (
            <select aria-label="Site" value={siteRef} onChange={(e) => setSiteRef(e.target.value)}>
              <option value="">Site…</option>
              {chosen.sites.map((s) => (
                <option key={s.ref} value={s.ref}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : null}
          <select aria-label="Relationship" value={status} onChange={(e) => setStatus(e.target.value as Relationship)}>
            {RELATIONSHIPS.map((r) => (
              <option key={r} value={r}>
                {label(r)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-small"
            disabled={approvalLevel === 'ad_space' && !siteRef}
            onClick={() => save({ accountRef: chosen.ref, siteRef: approvalLevel === 'ad_space' ? siteRef : null, relationshipStatus: status })}
          >
            Record relationship
          </button>
        </div>
      ) : null}
    </section>
  );
}

function describeTerms(terms: Record<string, unknown>): string {
  const value = terms['default'];
  if (typeof value !== 'number') return JSON.stringify(terms);
  return terms['type'] === 'fixed' ? `${String(terms['currency'] ?? '')} ${value}`.trim() : `${value}%`;
}

/** Adds a dated deal; earlier deals stay as history. */
function AddTerm({ membershipId }: { membershipId: number }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState('');
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  if (!open) {
    return (
      <button type="button" className="btn btn-ghost btn-small" onClick={() => setOpen(true)}>
        New deal
      </button>
    );
  }
  return (
    <span className="aff-inline">
      <input type="number" min={0} step="0.01" aria-label="Commission %" placeholder="%" value={rate} onChange={(e) => setRate(e.target.value)} />
      <input type="date" aria-label="Valid from" value={from} onChange={(e) => setFrom(e.target.value)} />
      <button
        type="button"
        className="btn btn-small"
        disabled={rate === ''}
        onClick={async () => {
          const ok = await runWrite(toast, 'admin.programs.addCommissionTerm', { membershipId, terms: { type: 'percent', default: Number(rate) }, validFrom: from, source: 'negotiated' }, 'Deal recorded');
          if (ok) setOpen(false);
        }}
      >
        Save deal
      </button>
    </span>
  );
}
